import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import React, { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Order } from '../../entities/order/Order'
import Modal from '../modal/Modal'
import ProductItemComponent from '../product-item/ProductItem'
import CycleHeader from '../cycle-header/CycleHeader'
import messages from './OrderConsolidateModal.messages'
import CheckboxMui from '@mui/material/Checkbox'
import { OrderProduct } from '../../entities/order-product/OrderProduct'

export interface IOwnProps {
  order: Order
  open: boolean
  onClose: () => void
}

export interface IStateProps {
}

export interface IDispatchProps {
  requestCreateLabels: (products: OrderProduct[]) => void
}

export type IProps = IOwnProps & IStateProps & IDispatchProps;

const OrderTitle = styled('div')(({ theme }) => ({
  color: theme.palette.blackColor.light,
  fontSize: 25,
  margin: 0,
  marginBottom: theme.spacing(3),
  '& span': {
    fontWeight: 700,
  },
}))

const OrderContainer = styled('div')({
  padding: '25px 12px 16px 12px',
})

const ProductCheckboxContainer = styled('div')({
  flexShrink: 0,
  marginRight: 9,
})

const ProductList = styled('ul')({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  minHeight: 300,
  marginBottom: 16,
})

const ProductListItem = styled('li')(({ theme }) => ({
  display: 'flex',
  '& + &': {
    marginTop: theme.spacing(4),
  },
}))

const ProductItem = styled(ProductItemComponent)({
  flexGrow: 1,
})

const Checkbox = styled(CheckboxMui)(({disabled}) => ({
  padding: 0,
  opacity: disabled ? 0.2 : undefined,
  '.MuiSvgIcon-root': {
    fontSize: 38,
  },
}))

const UncheckedIcon = styled('span')({
  width: 26.5,
  height: 26.5,
  border: '1px solid',
  borderRadius: 5,
  margin: 4.75,
  boxSizing: 'content-box',
})

const CycleHeaderContainer = styled('div')({
  borderBottom: '2px solid rgba(212, 212, 211, 0.44)',
  boxShadow: '0px 10px 5px 0px rgba(242, 242, 242, 0.27)',
})

const ModalHeaderContainer = styled('div')({
  fontSize: 18,
  lineHeight: '26px',
})

const OrderConsolidateModal: React.FC<IProps> = (props) => {
  const {
    order,
    open,
    onClose,
    requestCreateLabels,
  } = props

  const [canConsolidateProducts, setCanConsolidateProducts] = useState<number[]>([])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) {
      setCanConsolidateProducts([
        ...canConsolidateProducts,
        parseInt(event.currentTarget.value),
      ])
    } else {
      setCanConsolidateProducts(canConsolidateProducts.filter(item => item !== parseInt(event.currentTarget.value)))
    }
  }

  const handleClick = () => {
    requestCreateLabels(order.products.filter(({ id }) => canConsolidateProducts.includes(id)))
    onClose()
  }

  const isAnyProductsCanConsolidate = canConsolidateProducts.length > 0
  return (
    <Modal
      modalName='order-consolidate'
      open={open}
      onClose={onClose}
      header={() =>
        <ModalHeaderContainer>
          <FormattedMessage {...messages.title} />
        </ModalHeaderContainer>
      }
      isWideContent
    >
      {order.cycle &&
        <CycleHeaderContainer>
          <CycleHeader
            cycle={order.cycle}
            active={true}
            extraLabeledProductAmount={canConsolidateProducts.length}
          />
        </CycleHeaderContainer>
      }

      <OrderContainer>
        <OrderTitle>
          #<span>{order.orderId}</span>
        </OrderTitle>

        <ProductList>
          {
            order.products.map(product => (
              <ProductListItem key={product.code}>
                <ProductCheckboxContainer>
                  <Checkbox
                    onChange={handleChange}
                    value={product.id}
                    disabled={!product.canConsolidate()}
                    color='default'
                    icon={<UncheckedIcon />}
                    checked={canConsolidateProducts.includes(product.id)}
                    disableRipple
                  />
                </ProductCheckboxContainer>

                <ProductItem
                  product={product}
                  shouldShowControls={false}
                  shouldOpenModal={false}
                />
              </ProductListItem>
            ))
          }
        </ProductList>

        <Button
          variant='contained'
          color='primary'
          disabled={!isAnyProductsCanConsolidate}
          fullWidth
          onClick={handleClick}
        >
          {isAnyProductsCanConsolidate
            ? <FormattedMessage {...messages.consolidateItems} values={{ amount: canConsolidateProducts.length }} />

            : <FormattedMessage {...messages.button} />
          }
        </Button>
      </OrderContainer>
    </Modal>
  )
}

export default OrderConsolidateModal
